import { useCallback } from 'react';
import { prop, pick } from 'ramda';
import { useSetPlayerTags } from 'server';
import { useAppSelector } from 'js/store/hooks';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useFormatter } from 'js/hooks/useFormatter';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';

import * as helpers from './helpers';
import { fieldToImsValue } from '../../helpers';
import { useGetFieldId } from '../useGetFieldId';
import { useGetUserValues } from '../useGetUserValues';
import { ADDRESS_DETAILS_KEYS } from '../../constants';
import { useDynamicReducerSelector } from '../../store';

export const useOnCombinedSubmit = ({
    onSubmit,
    playerTags,
    submitTags,
    initialValues,
    fetchPlayerTags,
    setValuesAfterSubmit,
    requestReCaptchaToken,
    abortReCaptchaTokenRequest
}) => {
    const formatter = useFormatter();
    const isMounted = useIsMounted();
    const getErrorMessage = useGetErrorMessage();
    const setPlayerTags = useSetPlayerTags();
    const getFieldId = useGetFieldId();
    const getUserValues = useGetUserValues({ playerTags: [] });
    const allowedCountries = useAppSelector(prop('allowedCountries'));
    const { fields, hiddenByRulesFields, hiddenFields, formName } = useDynamicReducerSelector(pick([
        'fields', 'hiddenByRulesFields', 'hiddenFields', 'formName'
    ]));

    return useCallback(async (formData, form) => {
        if (isMounted()) {
            setValuesAfterSubmit(formData);
        }

        // region Common values
        const visibleFields = fields.filter((field) => !hiddenByRulesFields.includes(field.name));
        const innerFields = visibleFields
            .flatMap(field => 'innerFields' in field ? Object.values(field.innerFields) : []);
        const regularImsFields = visibleFields
            .filter((field) => field.imsField
                && !field.doNotSendToIMS
                && !field.usePlayerTags
                && !hiddenFields.includes(field.name)
                && field.type !== FORM_FIELD_TYPE.CONTACT_PREFERENCES
            );
        const innerImsFields = innerFields.filter((field) => field.imsField && !hiddenFields.includes(field.name));

        const imsFields = {
            ...helpers.getImsFieldsData({ fields: regularImsFields, formData, allowedCountries }),
            ...helpers.getImsFieldsData({ fields: innerImsFields, formData, allowedCountries })
        };
        const imsInitialFieldsData = {
            ...helpers.getImsFieldsData({ fields: regularImsFields, formData: initialValues, allowedCountries }),
            ...helpers.getImsFieldsData({ fields: innerImsFields, formData: initialValues, allowedCountries })
        };
        const changedImsFieldsData = helpers.getChangedFieldsData(imsInitialFieldsData, imsFields);
        // endregion

        // region Italian regulations
        if (visibleFields.some(field => field.type === FORM_FIELD_TYPE.PLACE_OF_BIRTH)) {
            /**
             * IMS workaround for Italian regulations:
             * For countryOfBirth other than Italy player can not select birthProvince and cityOfBirth
             * but IMS does not allow empty values so birthProvince should have value 'ee' and cityOfBirth - 'nocity'.
             */
            Object.assign(imsFields, {
                ...imsFields.birthProvince ? { birthProvince: imsFields.birthProvince } : { birthProvince: 'ee' },
                ...imsFields.cityOfBirth ? { cityOfBirth: imsFields.cityOfBirth } : { cityOfBirth: 'nocity' }
            });
        }

        if (visibleFields.some(field => field.type === FORM_FIELD_TYPE.PLACE_OF_RESIDENCE)) {
            /**
             * IMS workaround for Italian regulations:
             * For countryOfResidence other than Italy player can not select provinceOfResidence and cityOfResidence
             * but IMS does not allow empty values so provinceOfResidence should have value 'ee' and cityOfResidence - 'nocity'.
             * sogeiRegionCode is hidden field that depends on selected provinceOfResidence
             * which can not be selected for non-Italian residence therefor should default to "99".
             */
            Object.assign(imsFields, {
                ...imsFields.province ? { province: imsFields.province } : { province: 'ee' },
                ...imsFields.city ? { city: imsFields.city } : { city: 'nocity' },
                ...imsFields.sogeiRegionCode ? { sogeiRegionCode: imsFields.sogeiRegionCode } : { sogeiRegionCode: '99' }
            });
        }
        // endregion

        // region Address details
        const addressDetails = ADDRESS_DETAILS_KEYS
            .reduce((accumulator, key) => ({
                ...accumulator,
                [key]: imsFields[key]
            }), {});
        const changedAddressDetails = ADDRESS_DETAILS_KEYS
            .reduce((accumulator, key) => ({
                ...accumulator,
                ...changedImsFieldsData[key] !== undefined && { [key]: changedImsFieldsData[key] }
            }), {});
        // endregion

        // region Limits
        const betLimits = helpers.getBetLimitsData(formData);
        const lossLimits = helpers.getLossLimitsData(formData);
        const depositLimits = helpers.getDepositLimitsData(formData);
        // endregion

        // region Contact Preferences
        const contactPreferences = helpers.getContactPreferencesData(
            visibleFields.filter(field => field.type === FORM_FIELD_TYPE.CONTACT_PREFERENCES), formData, getFieldId
        );
       // endregion

        // region Player tags
        const getTagExpirationDate = (expirationDate) => {
            if (!expirationDate || Number.isNaN(expirationDate)) {
                return;
            }

            const date = new Date();

            date.setDate(date.getDate() + Number(expirationDate));

            return formatter.date(date.toISOString(), '%Y-%m-%d');
        };

        const changedPlayerTags = fields
            .filter((field) => field.usePlayerTags && !hiddenByRulesFields.includes(field.name) && !field.doNotSendToIMS)
            .reduceRight((accumulator, field) => {
                const { name, type, expirationDate } = field.playerTags[0];
                const playerTag = playerTags.find(tag => tag.name === name) || {};
                const newValue = fieldToImsValue(formData, field);
                const prevValue = playerTag.value;
                const newExpirationDate = getTagExpirationDate(expirationDate);

                return newValue === prevValue || accumulator.find(tag => tag.name === name)
                    ? accumulator
                    : [
                    ...accumulator,
                    {
                        name,
                        type,
                        value: newValue,
                        ...newExpirationDate && { expirationDate: newExpirationDate }
                    }
                ];
            }, []);

        if (submitTags && changedPlayerTags.length) {
            try {
                await setPlayerTags({ tags: changedPlayerTags });
                await fetchPlayerTags();
            } catch (error) {
                return getErrorMessage(error);
            }
        }
        // endregion

        const submitResult = await onSubmit({
            form,
            fields,
            formData,
            betLimits,
            getUserValues,
            lossLimits,
            submitTags,
            depositLimits,
            addressDetails,
            data: imsFields,
            contactPreferences,
            setValuesAfterSubmit,
            requestReCaptchaToken,
            changedAddressDetails,
            tags: changedPlayerTags,
            abortReCaptchaTokenRequest,
            dataMap: helpers.getDataWithoutAddressFields(imsFields),
            changedFieldsDataMap: helpers.getDataWithoutAddressFields(changedImsFieldsData)
        });

        // should be triggered only on success submit
        if (!submitResult) {
            publicEventsAPIService.trigger('dynamic-form-submitted', { formName });
        }

        return submitResult;
    }, [
        fields,
        formName,
        onSubmit,
        formatter,
        isMounted,
        getFieldId,
        playerTags,
        submitTags,
        hiddenFields,
        initialValues,
        getUserValues,
        setPlayerTags,
        fetchPlayerTags,
        getErrorMessage,
        allowedCountries,
        hiddenByRulesFields,
        setValuesAfterSubmit,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    ]);
};
